import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  icon: ReactNode;
  title: string;
  description?: string;
  required?: boolean;
  rightSlot?: ReactNode;
  children: ReactNode;
}

export function SectionCard({
  icon,
  title,
  description,
  required,
  rightSlot,
  children,
}: Props) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            {icon}
          </div>
          <div>
            <h2 className="text-base font-bold text-primary-700">{title}</h2>
            {description ? (
              <p className="mt-1 text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {required ? (
            <span className="rounded-md bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">
              {t("common.required")}
            </span>
          ) : null}
          {rightSlot}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

export function InfoCallout({
  children,
  variant = "info",
}: {
  children: ReactNode;
  variant?: "info" | "tip";
}) {
  const palette =
    variant === "tip"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : "bg-primary-50 text-primary-900 border-primary-100";
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${palette}`}
    >
      <span className="text-base leading-none">
        {variant === "tip" ? "💡" : "ℹ️"}
      </span>
      <div className="flex-1">{children}</div>
    </div>
  );
}
